import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react"; 
import { Helmet } from 'react-helmet-async';

// SERVER URL (enable these two lines when deploying):
const API_BASE_URL = 'https://www.ayushvatika.com/api';
const IMAGE_BASE_URL = 'https://www.ayushvatika.com/images/products/';
// // LOCALHOST URL (currently enabled):
// const API_BASE_URL = 'http://localhost:8484/api';
// const IMAGE_BASE_URL = 'http://localhost:8484/images/products/';

// Animation styles
const customStyles = `
  .no-scrollbar::-webkit-scrollbar { display: none; }
  .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
  
  @keyframes marquee {
    0% { transform: translateX(100%); }
    100% { transform: translateX(-100%); }
  }
  .animate-slow-marquee {
    display: inline-block;
    animation: marquee 12s linear infinite;
    will-change: transform;
  }
`;

const CategoryPage = () => {
  const navigate = useNavigate();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products`);
        if (!response.ok) {
          throw new Error(`Products API failed: ${response.status}`);
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          console.error('Expected an array of products, received:', data);
          throw new Error('Products API did not return a product array.');
        }

        const formatted = data.map((p: any) => {
          let imgPath = "default.png";
          if (p.imageUrl) {
            const list = p.imageUrl.split(',');
            imgPath = list[0].trim().replace(/[\[\]"]/g, '');
          }
          
          // LOGIC UPDATED HERE
          const discountPercent = p.discount || 0; 
          const originalPrice = p.mrp || p.price; // API's base price (e.g., 2790)
          
          // Calculate selling price with discount (e.g., 2790 - 12% = 2455)
          const sellingPrice = discountPercent > 0 
                ? Math.round(originalPrice - (originalPrice * discountPercent / 100)) 
                : originalPrice;

          const specialOffer = p.offer ? p.offer.toString() : ""; 

          return {
            id: p.id,
            slug: p.slug,
            name: p.productName,
            image: `${IMAGE_BASE_URL}${imgPath}`,
            price: sellingPrice,          // Final Green Price
            discount: discountPercent,    // Discount %
            originalPrice: originalPrice, // Original Strikethrough Price
            specialOffer: specialOffer
          };
        });
        setProducts(formatted);
      } catch (e) { console.error(e); } 
      finally { setIsLoading(false); }
    };
    fetchProducts();
  }, []);

  const scroll = (offset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  // Helper function to show placeholder cards if products are less than 4
  const renderPlaceholders = () => {
    const placeholders = [];
    const needed = Math.max(0, 4 - products.length);
    
    for (let i = 0; i < needed; i++) {
      placeholders.push(
        <div key={`placeholder-${i}`} className="flex-none w-[260px] md:w-[290px] bg-gray-50/50 rounded-xl border border-dashed border-gray-300 shadow-sm snap-start flex flex-col items-center justify-center p-6 opacity-70 group cursor-not-allowed">
          <div className="w-32 h-32 bg-gray-200 rounded-full mb-6 flex items-center justify-center text-gray-400">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
          </div>
          <h3 className="text-sm font-bold text-gray-500 mb-2">More Similar Products</h3>
          <p className="text-xs text-gray-400 bg-gray-200 px-3 py-1 rounded-full">Coming Soon</p>
        </div>
      );
    }
    return placeholders;
  };

  if (isLoading) return <div className="p-20 text-center font-bold text-green-700">Loading Ayush Vatika Products...</div>;

  return (
    <div className="bg-white py-10 px-4 md:px-12 mt-8">
      <style>{customStyles}</style>
      <Helmet><title>Trending Ayurvedic Medicines | Ayush Vatika</title></Helmet>

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 border-b-4 border-green-700 pb-1">
          Trending <span className="text-green-700">AyushVatika</span> Products
        </h2>
        <button onClick={() => navigate("/all-products")} className="text-sm font-bold text-gray-500 hover:text-green-700 uppercase transition-colors">
          View All
        </button>
      </div>

      {/* Main Layout container: Split into Banner (Left) and Slider (Right) */}
      <div className="flex flex-col lg:flex-row gap-6 items-stretch">
        
        {/* LEFT SIDE: Promotional Image Banner */}
        <div className="w-full lg:w-1/3 rounded-2xl overflow-hidden shadow-lg relative group cursor-pointer hidden md:block">
           <img
              src="https://mazix.co.in/assets/hero-bg-BpQyeUuv.jpg"
              alt="Ayush Vatika Promo"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
           />
           {/* Gradient Overlay for Text */}
           <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6">
              <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full w-max mb-3">100% Natural</span>
              <h3 className="text-2xl font-bold text-white mb-2 leading-tight">Ayurvedic Health<br/>& Wellness</h3>
              <p className="text-sm text-gray-200 opacity-90">Explore our most trusted natural healing products for a better life.</p>
           </div>
        </div>

        {/* RIGHT SIDE: Product Slider Container */}
        <div className="w-full lg:w-2/3 relative group flex items-center">
          <button onClick={() => scroll(-330)} className="absolute -left-4 z-10 bg-white shadow-lg p-3 rounded-full border md:flex hidden hover:bg-green-50 hover:text-green-700 transition-colors">
            <ArrowLeft size={20} />
          </button>
          
          <button onClick={() => scroll(330)} className="absolute -right-4 z-10 bg-white shadow-lg p-3 rounded-full border md:flex hidden hover:bg-green-50 hover:text-green-700 transition-colors">
            <ArrowRight size={20} />
          </button>

          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto no-scrollbar py-4 px-2 snap-x w-full"
            style={{ scrollBehavior: 'smooth' }}
          >
            {/* Render Actual Products */}
            {products.map((product) => (
              <div
                key={product.id}
                className="flex-none w-[260px] md:w-[290px] bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 snap-start flex flex-col overflow-hidden group cursor-pointer"
               onClick={() => navigate(`/product/${product.slug}`)}
              >
                {/* Product Image Section */}
                <div className="relative h-[250px] w-full bg-gray-50 overflow-hidden">
                  {product.discount > 0 && (
                    <div className="absolute top-3 left-0 bg-red-600 text-white font-bold text-[10px] px-3 py-1 rounded-r-full shadow-md z-10">
                      {product.discount}% OFF
                    </div>
                  )}
                  
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Product Details Section */}
                <div className="p-4 flex flex-col flex-grow text-center bg-white border-t border-gray-50">
                  <h3 className="text-sm font-bold text-gray-800 line-clamp-2 min-h-[40px] mb-2">
                    {product.name}
                  </h3>

                  {/* UI UPDATED HERE TO MATCH PRODUCT DETAILS FORMAT */}
                  <div className="flex items-baseline justify-center mb-3">
                     <span className="text-xl font-extrabold text-green-600">
                        ₹{Number(product.price).toFixed(2)}
                     </span>
                     {product.originalPrice && (
                        <span className="ml-2 text-sm text-gray-500 line-through">
                           ₹{Number(product.originalPrice).toFixed(2)}
                        </span>
                     )}
                     {/* {product.discount > 0 && (
                        <span className="ml-2 text-sm font-semibold text-red-500">
                           {product.discount}% OFF
                        </span>
                     )} */}
                  </div>

                  {/* Marquee 1: Discount */}
                  {product.discount > 0 && (
                    <div className="w-full overflow-hidden h-6 mb-1 bg-red-50 flex items-center rounded-sm">
                      <p className="text-[10px] text-red-600 font-bold whitespace-nowrap animate-slow-marquee">
                        ⚡ Special {product.discount}% Discount Applied! Limited Time Offer ⚡
                      </p>
                    </div>
                  )}

                  {/* Marquee 2: Special Offer */}
                  {product.specialOffer && product.specialOffer.trim() !== "" && (
                    <div className="w-full overflow-hidden h-6 mb-4 bg-green-50 flex items-center rounded-sm">
                      <p className="text-[10px] text-green-700 font-bold whitespace-nowrap animate-slow-marquee">
                        🎁 SPECIAL DEAL: {product.specialOffer} 🎁
                      </p>
                    </div>
                  )}
                  
                  {(!product.specialOffer || product.specialOffer.trim() === "") && <div className="mb-4"></div>}

                  <button className="w-full bg-green-800 text-white py-2.5 rounded-lg font-bold text-xs uppercase hover:bg-green-900 transition-colors shadow-sm mt-auto">
                    Add to Bag
                  </button>
                </div>
              </div>
            ))}

            {/* Render Placeholders agar products 1 ya 2 hi hain */}
            {renderPlaceholders()}

          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;